
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardActions, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, ListItemText, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Add as AddIcon, AutoAwesome as AIIcon, Campaign as CampaignIcon, Delete as DeleteIcon, Pause as PauseIcon, PlayArrow as ResumeIcon, Send as SendIcon, Stop as StopIcon } from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AIService from "../services/AIService";
import AppService from "../services/AppService";
import EngagementService from "../services/EngagementService";
import type { App } from "../types/app";
import type { CreateEngagementCampaignDTO, EngagementCampaign, EngagementCampaignStatus, EngagementLocalizedMessage, EngagementSchedule } from "../types/engagement";

const ALL_USERS_TOPIC = "all_users";
const LOCALES = [
  { code: "en", name: "English", topic: "locale_en" },
  { code: "zh", name: "Chinese (Simplified)", topic: "locale_zh" },
  { code: "hi", name: "Hindi", topic: "locale_hi" },
  { code: "es", name: "Spanish", topic: "locale_es" },
  { code: "fr", name: "French", topic: "locale_fr" },
  { code: "ar", name: "Arabic", topic: "locale_ar" },
  { code: "bn", name: "Bengali", topic: "locale_bn" },
  { code: "pt", name: "Portuguese", topic: "locale_pt" },
  { code: "ru", name: "Russian", topic: "locale_ru" },
  { code: "ja", name: "Japanese", topic: "locale_ja" },
  { code: "de", name: "German", topic: "locale_de" },
  { code: "ko", name: "Korean", topic: "locale_ko" },
  { code: "it", name: "Italian", topic: "locale_it" },
  { code: "tr", name: "Turkish", topic: "locale_tr" },
  { code: "vi", name: "Vietnamese", topic: "locale_vi" },
] as const;
const WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
const OBJECTIVES = ["Increase DAU", "Win Back Inactive Users", "Promote New Feature Adoption", "Drive Subscription Upgrade", "Reduce Churn Risk", "Boost Session Frequency", "Seasonal Promotion", "Custom"] as const;

type LocaleCode = (typeof LOCALES)[number]["code"];
type ScheduleType = "send_now" | "one_time" | "recurring";

type FormState = {
  appId: string;
  name: string;
  objective: string;
  objectiveCustom: string;
  baseTitle: string;
  baseBody: string;
  targetingMode: "all_users" | "multi_locale";
  localeCodes: LocaleCode[];
  localizedMessages: Record<string, EngagementLocalizedMessage>;
  scheduleType: ScheduleType;
  timezone: string;
  startAt: string;
  endAt: string;
  frequency: "daily" | "weekly" | "monthly";
  interval: number;
  weekdays: string[];
  dayOfMonth: number;
};

const getDefaultTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};
const toDateTimeInput = (date: Date) => {
  const copy = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return copy.toISOString().slice(0, 16);
};
const formatDate = (value?: Date) => {
  if (!value) return "-";
  try { return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(value); } catch { return value.toISOString(); }
};

const INITIAL_FORM: FormState = {
  appId: "", name: "", objective: "", objectiveCustom: "", baseTitle: "", baseBody: "", targetingMode: "all_users", localeCodes: [], localizedMessages: {},
  scheduleType: "send_now", timezone: getDefaultTimezone(), startAt: toDateTimeInput(new Date(Date.now() + 5 * 60 * 1000)), endAt: "", frequency: "daily", interval: 1, weekdays: ["MO"], dayOfMonth: 1,
};

const AppEngagement: React.FC = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [campaigns, setCampaigns] = useState<EngagementCampaign[]>([]);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBaseLoading, setAiBaseLoading] = useState(false);
  const [aiLocaleLoading, setAiLocaleLoading] = useState(false);
  const [dispatching, setDispatching] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | EngagementCampaignStatus>("all");

  const selectedApp = useMemo(() => apps.find((a) => a.appId === form.appId) || null, [apps, form.appId]);
  const selectedLocaleMeta = useMemo(() => LOCALES.filter((l) => form.localeCodes.includes(l.code)), [form.localeCodes]);
  const filteredCampaigns = useMemo(() => campaigns.filter((c) => (statusFilter === "all" ? true : c.status === statusFilter)), [campaigns, statusFilter]);
  const dashboardStats = useMemo(() => ({
    total: campaigns.length,
    drafts: campaigns.filter((item) => item.status === "draft").length,
    scheduled: campaigns.filter((item) => item.status === "scheduled").length,
    paused: campaigns.filter((item) => item.status === "paused").length,
    sent: campaigns.filter((item) => item.status === "sent").length,
    retryPending: campaigns.filter((item) => (item.retryState?.failedTargets?.length || 0) > 0).length,
  }), [campaigns]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, localizedMessages: { ...prev.localizedMessages, en: { title: prev.baseTitle, body: prev.baseBody } } }));
  }, [form.baseTitle, form.baseBody]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const result = await AppService.getUserApps(user.email);
    setApps(result);
    setForm((prev) => ({ ...prev, appId: prev.appId || result[0]?.appId || "" }));
  }, [user?.email]);

  const loadCampaigns = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try { setCampaigns(await EngagementService.listUserCampaigns(user.email)); } finally { setLoading(false); }
  }, [user?.email]);

  useEffect(() => {
    loadApps().catch((e) => setError(e?.message || "Failed to load apps."));
    loadCampaigns().catch((e) => setError(e?.message || "Failed to load campaigns."));
  }, [loadApps, loadCampaigns]);
  const objectiveValue = form.objective === "Custom" ? form.objectiveCustom.trim() : form.objective.trim();

  const validate = (): string | null => {
    if (!form.appId) return "Select a registered app.";
    if (!selectedApp?.firebaseProjectId) return "Selected app has no Firebase Project ID. Add it in App Registration so FCM can route messages to the correct project.";
    if (!form.name.trim()) return "Campaign name is required.";
    if (!objectiveValue) return "Campaign objective is required.";
    if (!form.baseTitle.trim() || !form.baseBody.trim()) return "Base English copy is required.";
    if (!form.timezone.trim()) return "Timezone is required.";

    if (form.targetingMode === "multi_locale") {
      if (!form.localeCodes.length) return "Select at least one target locale.";
      for (const code of form.localeCodes) {
        const copy = form.localizedMessages[code];
        if (!copy?.title?.trim() || !copy?.body?.trim()) return `Locale copy is incomplete for ${code}.`;
      }
    }

    if (form.scheduleType !== "send_now") {
      const startAt = new Date(form.startAt);
      if (Number.isNaN(startAt.getTime())) return "Valid start date/time is required.";
    }

    if (form.scheduleType === "recurring") {
      if (form.interval < 1) return "Recurrence interval must be at least 1.";
      if (form.frequency === "weekly" && !form.weekdays.length) return "Select at least one weekday for weekly recurrence.";
      if (form.frequency === "monthly" && (form.dayOfMonth < 1 || form.dayOfMonth > 31)) return "Monthly day must be between 1 and 31.";
      if (form.endAt) {
        const endAt = new Date(form.endAt);
        const startAt = new Date(form.startAt);
        if (Number.isNaN(endAt.getTime())) return "End date/time is invalid.";
        if (endAt <= startAt) return "End date/time must be after start date/time.";
      }
    }

    return null;
  };

  const generateBaseCopy = async () => {
    if (!objectiveValue) { setError("Select campaign objective first."); return; }
    setAiBaseLoading(true);
    try {
      const result = await AIService.generateJSON<{ campaignName: string; title: string; body: string }>(
        `Generate English push campaign JSON with keys campaignName,title,body. Objective: ${objectiveValue}. Keep title <=55 chars and body <=140 chars.`,
        { model: AIService.getDefaultModel(), temperature: 0.5, maxTokens: 350 },
      );
      setForm((prev) => ({ ...prev, name: result.campaignName || prev.name, baseTitle: result.title || prev.baseTitle, baseBody: result.body || prev.baseBody }));
      setSuccess("Generated English base copy.");
    } catch (e: any) {
      setError(e?.message || "Failed to generate base copy.");
    } finally {
      setAiBaseLoading(false);
    }
  };

  const generateLocaleCopy = async () => {
    if (form.targetingMode !== "multi_locale") return;
    if (!form.baseTitle.trim() || !form.baseBody.trim()) { setError("Set base English copy first."); return; }
    if (!form.localeCodes.length) { setError("Select target locales first."); return; }

    const toTranslate = selectedLocaleMeta.filter((l) => l.code !== "en");
    if (!toTranslate.length) return;
    setAiLocaleLoading(true);
    try {
      const result = await AIService.generateJSON<{ localizedEntries: Array<{ locale: string; title: string; body: string }> }>([
        "Translate this English push notification to locales and return JSON with localizedEntries[].",
        `English title: ${form.baseTitle}`,
        `English body: ${form.baseBody}`,
        `Locales: ${toTranslate.map((l) => l.code).join(", ")}`,
      ].join("\n"), { model: AIService.getDefaultModel(), temperature: 0.4, maxTokens: 1800 });

      const mapped = Object.fromEntries((result.localizedEntries || []).map((item) => [item.locale, { title: String(item.title || ""), body: String(item.body || "") }]));
      setForm((prev) => ({ ...prev, localizedMessages: { ...prev.localizedMessages, ...mapped, en: { title: prev.baseTitle, body: prev.baseBody } } }));
      setSuccess("Generated localized copies.");
    } catch (e: any) {
      setError(e?.message || "Failed to generate localized copy.");
    } finally {
      setAiLocaleLoading(false);
    }
  };

  const buildSchedule = (): { schedule: EngagementSchedule; status: EngagementCampaignStatus } => {
    if (form.scheduleType === "send_now") {
      return { schedule: { type: "send_now", timezone: form.timezone, startAt: new Date() }, status: "draft" };
    }
    if (form.scheduleType === "one_time") {
      return { schedule: { type: "one_time", timezone: form.timezone, startAt: new Date(form.startAt) }, status: "scheduled" };
    }
    return {
      schedule: {
        type: "recurring",
        timezone: form.timezone,
        startAt: new Date(form.startAt),
        recurrence: {
          frequency: form.frequency,
          interval: form.interval,
          weekdays: form.frequency === "weekly" ? form.weekdays : undefined,
          dayOfMonth: form.frequency === "monthly" ? form.dayOfMonth : undefined,
        },
        endAt: form.endAt ? new Date(form.endAt) : undefined,
      },
      status: "scheduled",
    };
  };

  const createCampaign = async () => {
    if (!user?.email) return;
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    const app = apps.find((a) => a.appId === form.appId);
    if (!app) return;

    const localeTargets = form.targetingMode === "multi_locale"
      ? selectedLocaleMeta.map((l) => ({ languageCode: l.code, languageName: l.name, topic: l.topic, condition: `'${l.topic}' in topics` }))
      : undefined;

    const localizedMessages = form.targetingMode === "multi_locale"
      ? Object.fromEntries(form.localeCodes.map((code) => [code, form.localizedMessages[code] || { title: "", body: "" }]))
      : { en: { title: form.baseTitle, body: form.baseBody } };

    const { schedule, status } = buildSchedule();

    const dto: CreateEngagementCampaignDTO = {
      appId: app.appId,
      appName: app.name,
      firebaseProjectId: String(app.firebaseProjectId || ""),
      name: form.name.trim(),
      objective: objectiveValue,
      status,
      message: { title: form.baseTitle.trim(), body: form.baseBody.trim() },
      localizedMessages,
      targeting: {
        mode: form.targetingMode,
        allUsersTopic: ALL_USERS_TOPIC,
        condition: form.targetingMode === "all_users" ? `'${ALL_USERS_TOPIC}' in topics` : "per-locale-topic-dispatch",
        localeTargets,
      },
      schedule,
    };

    setSaving(true);
    try {
      const created = await EngagementService.createCampaign(user.email, dto);
      if (form.scheduleType === "send_now") {
        await EngagementService.dispatchCampaign(created.campaignId);
      }
      setDialogOpen(false);
      setForm({ ...INITIAL_FORM, appId: apps[0]?.appId || "" });
      await loadCampaigns();
      setSuccess(form.scheduleType === "send_now" ? "Campaign sent immediately." : "Campaign created.");
    } catch (e: any) {
      setError(e?.message || "Failed to create campaign.");
    } finally {
      setSaving(false);
    }
  };

  const updateLocaleField = (code: string, key: "title" | "body", value: string) => {
    setForm((prev) => ({
      ...prev,
      localizedMessages: {
        ...prev.localizedMessages,
        [code]: {
          title: prev.localizedMessages[code]?.title || "",
          body: prev.localizedMessages[code]?.body || "",
          [key]: value,
        },
      },
    }));
  };

  const runDispatch = async (campaign: EngagementCampaign, mode: "send" | "dry") => {
    setDispatching((prev) => ({ ...prev, [campaign.campaignId]: mode }));
    setError(null);
    try {
      const result = await EngagementService.dispatchCampaign(campaign.campaignId, { dryRun: mode === "dry" });
      await loadCampaigns();
      const failuresCount = result.failures?.length || 0;
      setSuccess(mode === "dry"
        ? `Dry run completed: ${result.sentCount} target(s) validated${failuresCount ? `, ${failuresCount} failure(s)` : ""}.`
        : `Campaign dispatched: ${result.sentCount} target(s) sent${failuresCount ? `, ${failuresCount} failure(s)` : ""}.`);
    } catch (sendError: any) {
      setError(sendError?.message || (mode === "dry" ? "Failed to run dry run dispatch." : "Failed to dispatch campaign."));
    } finally {
      setDispatching((prev) => ({ ...prev, [campaign.campaignId]: null }));
    }
  };

  const runStatusAction = async (campaign: EngagementCampaign, action: "pause" | "resume" | "stop") => {
    setDispatching((prev) => ({ ...prev, [campaign.campaignId]: `${action}:${campaign.campaignId}` }));
    setError(null);
    try {
      if (action === "pause") await EngagementService.updateCampaign(campaign.campaignId, { status: "paused" });
      else if (action === "resume") await EngagementService.updateCampaign(campaign.campaignId, { status: "scheduled" });
      else await EngagementService.updateCampaign(campaign.campaignId, { status: "archived" });
      await loadCampaigns();
      setSuccess(`Campaign ${action}d successfully.`);
    } catch (actionError: any) {
      setError(actionError?.message || `Failed to ${action} campaign.`);
    } finally {
      setDispatching((prev) => ({ ...prev, [campaign.campaignId]: null }));
    }
  };
  return (
    <Box>
      <Paper sx={{ p: { xs: 2.5, md: 3 }, mb: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", background: "linear-gradient(135deg, rgba(2,132,199,0.14) 0%, rgba(255,255,255,1) 52%, rgba(14,165,233,0.08) 100%)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>App Engagement</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Build one-time and recurring FCM campaigns with locale targeting and scheduler-backed delivery.</Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ borderRadius: 2.5, px: 2.5 }}>New Engagement Campaign</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      <Alert severity="info" sx={{ mb: 2 }}>
        To send messages for a registered app, grant this App Success runtime service account access in that app&apos;s Firebase/GCP project IAM: <strong>868717168128-compute@developer.gserviceaccount.com</strong>. Recommended role: <strong>Firebase Cloud Messaging Admin</strong>.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[{ label: "Total", value: dashboardStats.total }, { label: "Scheduled", value: dashboardStats.scheduled }, { label: "Paused", value: dashboardStats.paused }, { label: "Sent", value: dashboardStats.sent }, { label: "Retry Pending", value: dashboardStats.retryPending }, { label: "Drafts", value: dashboardStats.drafts }].map((item) => (
          <Grid item xs={12} sm={6} md={2} key={item.label}><Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}><Typography variant="caption" color="text.secondary">{item.label}</Typography><Typography variant="h5" sx={{ fontWeight: 800 }}>{item.value}</Typography></Paper></Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.2, mb: 2, borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {["all", "draft", "scheduled", "paused", "sent", "archived", "failed"].map((item) => (
            <Button key={item} size="small" variant={statusFilter === item ? "contained" : "outlined"} onClick={() => setStatusFilter(item as "all" | EngagementCampaignStatus)}>{item === "all" ? "All" : item}</Button>
          ))}
        </Stack>
      </Paper>

      {loading ? <CircularProgress /> : filteredCampaigns.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, bgcolor: "grey.50" }}>
          <CampaignIcon sx={{ fontSize: 36, color: "text.secondary", mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>No campaigns in this view</Typography>
          <Typography variant="body2" color="text.secondary">Create a new campaign or change the status filter to see more results.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredCampaigns.map((campaign) => {
            const actionState = dispatching[campaign.campaignId];
            const hasRetry = (campaign.retryState?.failedTargets?.length || 0) > 0;
            return (
              <Grid item xs={12} md={6} key={campaign.campaignId}>
                <Card sx={{ borderRadius: 2.5, border: "1px solid", borderColor: "divider", boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{campaign.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{campaign.appName}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Objective: {campaign.objective || "Not set"}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={campaign.status} color={campaign.status === "sent" ? "success" : "default"} />
                      <Chip size="small" variant="outlined" label={campaign.targeting.mode} />
                      <Chip size="small" variant="outlined" label={campaign.schedule?.type || "send_now"} />
                      {campaign.targeting.mode === "multi_locale" ? <Chip size="small" variant="outlined" label={`Locales: ${campaign.targeting.localeTargets?.length || 0}`} /> : null}
                      {hasRetry ? <Chip size="small" color="warning" label="Retry pending" /> : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Next send: {formatDate(campaign.nextRunAt)} | Last sent: {formatDate(campaign.lastSentAt)} | Runs: {campaign.runCount || 0}
                    </Typography>
                    {campaign.lastRunSummary ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Last run: attempted {campaign.lastRunSummary.attempted}, sent {campaign.lastRunSummary.sent}, failed {campaign.lastRunSummary.failed}</Typography> : null}
                  </CardContent>
                  <CardActions sx={{ flexWrap: "wrap", gap: 0.5 }}>
                    <Button size="small" startIcon={<SendIcon />} disabled={Boolean(actionState)} onClick={() => runDispatch(campaign, "send")}>{actionState === "send" ? "Sending..." : "Send now"}</Button>
                    <Button size="small" variant="outlined" disabled={Boolean(actionState)} onClick={() => runDispatch(campaign, "dry")}>{actionState === "dry" ? "Dry Running..." : "Dry Run"}</Button>
                    {campaign.status === "scheduled" ? <Button size="small" startIcon={<PauseIcon />} disabled={Boolean(actionState)} onClick={() => runStatusAction(campaign, "pause")}>Pause</Button> : null}
                    {campaign.status === "paused" ? <Button size="small" startIcon={<ResumeIcon />} disabled={Boolean(actionState)} onClick={() => runStatusAction(campaign, "resume")}>Resume</Button> : null}
                    {campaign.schedule?.type === "recurring" && (campaign.status === "scheduled" || campaign.status === "paused") ? <Button size="small" color="warning" startIcon={<StopIcon />} disabled={Boolean(actionState)} onClick={() => runStatusAction(campaign, "stop")}>Stop recurring</Button> : null}
                    <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={Boolean(actionState)} onClick={() => EngagementService.deleteCampaign(campaign.campaignId).then(loadCampaigns)}>Delete</Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Create App Engagement Campaign</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Registered app" value={form.appId} onChange={(e) => setForm((p) => ({ ...p, appId: e.target.value }))}>{apps.map((app) => <MenuItem key={app.appId} value={app.appId}>{app.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Campaign name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField select fullWidth label="Campaign objective" value={form.objective} onChange={(e) => setForm((p) => ({ ...p, objective: e.target.value }))}>{OBJECTIVES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</TextField></Grid>
              {form.objective === "Custom" ? <Grid item xs={12}><TextField fullWidth label="Custom objective" value={form.objectiveCustom} onChange={(e) => setForm((p) => ({ ...p, objectiveCustom: e.target.value }))} /></Grid> : null}
            </Grid>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Base English Copy</Typography>
                <Button variant="outlined" startIcon={aiBaseLoading ? <CircularProgress size={14} /> : <AIIcon />} onClick={generateBaseCopy} disabled={aiBaseLoading}>Generate Base Copy</Button>
              </Stack>
              <Stack spacing={1.5}>
                <TextField fullWidth label="English title" value={form.baseTitle} onChange={(e) => setForm((p) => ({ ...p, baseTitle: e.target.value }))} />
                <TextField fullWidth multiline minRows={2} label="English body" value={form.baseBody} onChange={(e) => setForm((p) => ({ ...p, baseBody: e.target.value }))} />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Schedule</Typography>
              <Stack spacing={1.5}>
                <ToggleButtonGroup exclusive value={form.scheduleType} onChange={(_, v) => v && setForm((p) => ({ ...p, scheduleType: v }))}>
                  <ToggleButton value="send_now">Send now</ToggleButton>
                  <ToggleButton value="one_time">One-time</ToggleButton>
                  <ToggleButton value="recurring">Recurring</ToggleButton>
                </ToggleButtonGroup>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Timezone" value={form.timezone} onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))} /></Grid>
                  {form.scheduleType !== "send_now" ? <Grid item xs={12} md={6}><TextField fullWidth type="datetime-local" label="Start at" InputLabelProps={{ shrink: true }} value={form.startAt} onChange={(e) => setForm((p) => ({ ...p, startAt: e.target.value }))} /></Grid> : null}
                </Grid>
                {form.scheduleType === "recurring" ? (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField select fullWidth label="Frequency" value={form.frequency} onChange={(e) => setForm((p) => ({ ...p, frequency: e.target.value as "daily" | "weekly" | "monthly" }))}><MenuItem value="daily">Daily</MenuItem><MenuItem value="weekly">Weekly</MenuItem><MenuItem value="monthly">Monthly</MenuItem></TextField></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="Interval" type="number" inputProps={{ min: 1 }} value={form.interval} onChange={(e) => setForm((p) => ({ ...p, interval: Math.max(1, Number(e.target.value || 1)) }))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="datetime-local" label="Optional end at" InputLabelProps={{ shrink: true }} value={form.endAt} onChange={(e) => setForm((p) => ({ ...p, endAt: e.target.value }))} /></Grid>
                    </Grid>
                    {form.frequency === "weekly" ? <FormControl fullWidth><TextField select label="Weekdays" SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).join(", ") }} value={form.weekdays} onChange={(e) => { const raw = e.target.value; const values = typeof raw === "string" ? raw.split(",") : (raw as string[]); setForm((p) => ({ ...p, weekdays: values.filter(Boolean) })); }}>{WEEKDAYS.map((day) => <MenuItem key={day} value={day}><Checkbox checked={form.weekdays.includes(day)} /><ListItemText primary={day} /></MenuItem>)}</TextField></FormControl> : null}
                    {form.frequency === "monthly" ? <TextField fullWidth label="Day of month" type="number" inputProps={{ min: 1, max: 31 }} value={form.dayOfMonth} onChange={(e) => setForm((p) => ({ ...p, dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value || 1))) }))} /> : null}
                  </>
                ) : null}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Targeting</Typography>
              <Stack spacing={1.5}>
                <ToggleButtonGroup exclusive value={form.targetingMode} onChange={(_, v) => v && setForm((p) => ({ ...p, targetingMode: v }))}>
                  <ToggleButton value="all_users">All users</ToggleButton>
                  <ToggleButton value="multi_locale">Multi-locale</ToggleButton>
                </ToggleButtonGroup>
                {form.targetingMode === "multi_locale" ? (
                  <>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => setForm((p) => ({ ...p, localeCodes: LOCALES.map((x) => x.code) as LocaleCode[] }))}>Select all locales</Button>
                      <Button size="small" variant="outlined" onClick={() => setForm((p) => ({ ...p, localeCodes: [] }))}>Clear locales</Button>
                    </Stack>
                    <FormControl fullWidth>
                      <TextField select label="Target locales" SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).join(", ") }} value={form.localeCodes} onChange={(e) => { const raw = e.target.value; const values = typeof raw === "string" ? raw.split(",") : (raw as string[]); setForm((p) => ({ ...p, localeCodes: values.filter((value): value is LocaleCode => LOCALES.some((locale) => locale.code === value)) })); }}>
                        {LOCALES.map((locale) => <MenuItem key={locale.code} value={locale.code}><Checkbox checked={form.localeCodes.includes(locale.code)} /><ListItemText primary={`${locale.name} (${locale.topic})`} /></MenuItem>)}
                      </TextField>
                    </FormControl>
                    <Button variant="outlined" startIcon={aiLocaleLoading ? <CircularProgress size={14} /> : <AIIcon />} onClick={generateLocaleCopy} disabled={aiLocaleLoading}>Generate Locale Copy From English</Button>
                    {selectedLocaleMeta.map((locale) => (
                      <Paper key={locale.code} variant="outlined" sx={{ p: 1.2 }}>
                        <Chip size="small" label={`${locale.name} -> ${locale.topic}`} sx={{ mb: 1 }} />
                        <Stack spacing={1}>
                          <TextField fullWidth label={`${locale.code} title`} value={form.localizedMessages[locale.code]?.title || ""} onChange={(e) => updateLocaleField(locale.code, "title", e.target.value)} />
                          <TextField fullWidth multiline minRows={2} label={`${locale.code} body`} value={form.localizedMessages[locale.code]?.body || ""} onChange={(e) => updateLocaleField(locale.code, "body", e.target.value)} />
                        </Stack>
                      </Paper>
                    ))}
                  </>
                ) : null}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createCampaign} disabled={saving || aiBaseLoading || aiLocaleLoading}>{saving ? "Creating..." : "Create Campaign"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppEngagement;
